import React, { useState, useEffect, useRef } from 'react';
import {
    Typography,
    TextField,
    Button,
    Box,
    Card,
    CardContent,
    Checkbox,
    FormControlLabel,
    Tooltip,
    Link,
    CircularProgress,
    makeStyles
} from '@material-ui/core';
import { CreditCard, ArrowBack } from '@material-ui/icons';
import { useStateValue } from '../../context/state/provider';
import { WIZARD_STEPS } from '../../constants/wizardSteps.constants';
import { VIEW } from '../../constants/view.constants';
import { publishAgent, sendVerificationCode, verifyEmailCode } from '../../services/api';
import { checkEmailSubscription } from '../../services/emailSubscriptionCheck';

const useStyles = makeStyles((theme) => ({
    stepContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(2.5),
        minHeight: 400
    },
    header: {
        textAlign: 'center',
        marginBottom: theme.spacing(0.5)
    },
    title: {
        fontSize: '1.35rem',
        fontWeight: 600,
        color: theme.palette.text.primary,
        letterSpacing: '-0.01em',
        marginBottom: theme.spacing(0.75)
    },
    description: {
        color: theme.palette.text.secondary,
        fontSize: '0.95rem',
        lineHeight: 1.5,
        maxWidth: 460,
        margin: '0 auto'
    },
    formCard: {
        padding: theme.spacing(1, 0.5),
        marginBottom: 0,
        border: '1px solid #E5E7EB',
        borderRadius: 12,
        boxShadow: 'none'
    },
    sectionLabel: {
        fontSize: '0.8rem',
        fontWeight: 600,
        letterSpacing: '0.04em',
        textTransform: 'uppercase',
        color: theme.palette.text.secondary,
        marginBottom: theme.spacing(1.5)
    },
    formRow: {
        display: 'flex',
        gap: theme.spacing(2),
        marginBottom: theme.spacing(2),
        [theme.breakpoints.down('sm')]: {
            flexDirection: 'column',
            gap: theme.spacing(1)
        }
    },
    textField: {
        flex: 1
    },
    fullWidthField: {
        marginBottom: theme.spacing(1)
    },
    infoBox: {
        padding: theme.spacing(2, 2.5),
        backgroundColor: '#F8FAFC',
        borderRadius: 12,
        border: '1px solid #E5E7EB'
    },
    infoTitle: {
        fontSize: '0.875rem',
        fontWeight: 600,
        color: theme.palette.text.primary,
        marginBottom: theme.spacing(1)
    },
    infoList: {
        margin: 0,
        paddingLeft: theme.spacing(2.25),
        color: theme.palette.text.secondary,
        fontSize: '0.875rem',
        lineHeight: 1.6,
        '& li + li': {
            marginTop: theme.spacing(0.5)
        }
    },
    termsCard: {
        padding: theme.spacing(0.5, 1),
        border: 'none',
        boxShadow: 'none',
        backgroundColor: 'transparent'
    },
    buttonContainer: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: theme.spacing(1.5),
        marginTop: theme.spacing(0.5),
        flexWrap: 'wrap'
    },
    leftActions: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(0.5)
    },
    backButton: {
        color: theme.palette.text.secondary,
        fontSize: '0.875rem',
        fontWeight: 500,
        padding: theme.spacing(1, 1.5),
        border: 'none',
        minWidth: 0,
        '&:hover': {
            backgroundColor: 'transparent',
            color: theme.palette.primary.main
        }
    },
    helpButton: {
        color: theme.palette.text.secondary,
        fontSize: '0.875rem',
        fontWeight: 500,
        padding: theme.spacing(1, 1.5),
        border: 'none',
        minWidth: 0,
        '&:hover': {
            backgroundColor: 'transparent',
            color: theme.palette.primary.main
        }
    },
    publishButton: {
        minWidth: 160,
        padding: theme.spacing(1.25, 3),
        fontSize: '0.95rem',
        fontWeight: 600,
        borderRadius: 8,
        boxShadow: 'none',
        '&:hover': {
            boxShadow: 'none'
        }
    },
    customTooltip: {
        backgroundColor: theme.palette.grey[800],
        color: theme.palette.common.white,
        fontSize: '12px',
        borderRadius: theme.spacing(0.5),
        padding: theme.spacing(1),
        maxWidth: 200
    },
    tooltipArrow: {
        display: 'none'
    },
    prefilledEmailBox: {
        padding: theme.spacing(2),
        backgroundColor: '#F8FAFC',
        borderRadius: 10,
        marginBottom: theme.spacing(1),
        border: '1px solid #E5E7EB',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
    },
    prefilledEmailText: {
        color: theme.palette.text.primary,
        fontWeight: 600,
        fontSize: '15px'
    },
    prefilledEmailSubtext: {
        color: theme.palette.text.secondary,
        fontSize: '13px',
        marginTop: theme.spacing(0.5)
    },
    adminBadge: {
        backgroundColor: '#EEF2FF',
        color: theme.palette.primary.main,
        padding: theme.spacing(0.5, 1.25),
        borderRadius: 999,
        fontSize: '12px',
        fontWeight: 600
    },
    verificationSection: {
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(0.5),
        padding: theme.spacing(2),
        backgroundColor: '#F8FAFC',
        borderRadius: 10,
        border: '1px solid #E5E7EB'
    },
    verificationTitle: {
        fontSize: '0.95rem',
        fontWeight: 600,
        color: theme.palette.text.primary,
        marginBottom: theme.spacing(1.5),
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: theme.spacing(2),
        [theme.breakpoints.down('sm')]: {
            flexDirection: 'column',
            alignItems: 'flex-start'
        }
    },
    codeInputContainer: {
        display: 'flex',
        gap: theme.spacing(1.5),
        alignItems: 'flex-start',
        marginBottom: theme.spacing(2),
        [theme.breakpoints.down('sm')]: {
            flexDirection: 'column',
            gap: theme.spacing(1)
        }
    },
    codeInput: {
        flex: 1,
        '& .MuiOutlinedInput-root': {
            backgroundColor: 'white',
            fontSize: '18px',
            fontFamily: '"Courier New", monospace',
            letterSpacing: '2px',
            textAlign: 'center'
        }
    },
    verifyButton: {
        minWidth: 110,
        height: 56,
        backgroundColor: theme.palette.primary.main,
        color: 'white',
        fontWeight: 600,
        boxShadow: 'none',
        '&:hover': {
            backgroundColor: theme.palette.primary.dark,
            boxShadow: 'none'
        }
    },
    resendButton: {
        minWidth: 100,
        height: 56,
        color: theme.palette.text.secondary,
        borderColor: '#E5E7EB',
        '&:hover': {
            backgroundColor: '#F8FAFC',
            borderColor: '#D1D5DB'
        }
    },
    sendCodeButton: {
        backgroundColor: theme.palette.primary.main,
        color: 'white',
        fontWeight: 600,
        padding: theme.spacing(1.25, 2.5),
        boxShadow: 'none',
        '&:hover': {
            backgroundColor: theme.palette.primary.dark,
            boxShadow: 'none'
        }
    },
    verificationSuccess: {
        padding: theme.spacing(1.5),
        backgroundColor: '#F0FDF4',
        border: '1px solid #BBF7D0',
        borderRadius: 8,
        color: '#166534',
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        marginBottom: theme.spacing(1)
    },
    verificationError: {
        padding: theme.spacing(1.5),
        backgroundColor: '#FEF2F2',
        border: '1px solid #FECACA',
        borderRadius: 8,
        color: '#991B1B',
        marginTop: theme.spacing(1)
    },
    verificationInstructions: {
        fontSize: '14px',
        color: theme.palette.text.secondary,
        marginBottom: theme.spacing(2),
        lineHeight: 1.5
    },
    publishingOverlay: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
        gap: theme.spacing(2)
    },
    publishingMessage: {
        fontSize: '24px',
        fontWeight: 600,
        color: theme.palette.primary.main
    }
}));

export default function UserDetailsStep() {
    const classes = useStyles();
    const [{ wizardData }, dispatch] = useStateValue();
    const [userDetails, setUserDetails] = useState(wizardData.userDetails || {
        firstName: '',
        lastName: '',
        email: '',
        company: ''
    });
    const [agreedToTerms, setAgreedToTerms] = useState(false);
    const [errors, setErrors] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const containerRef = useRef(null);

    // Prevent auto-scroll on component mount
    useEffect(() => {
        if (containerRef.current) {
            // Scroll to the top of the container
            window.scrollTo({ top: 0, behavior: 'instant' });
        }
    }, []);

    const validateEmail = (email) => {
        const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return re.test(email);
    };

    const handleInputChange = (field) => (event) => {
        const value = event.target.value;
        setUserDetails(prev => ({
            ...prev,
            [field]: value
        }));

        // Clear error when user starts typing
        if (errors[field]) {
            setErrors(prev => ({
                ...prev,
                [field]: ''
            }));
        }
    };



    const handleBackToTest = () => {
        dispatch({
            type: 'UPDATE_WIZARD_DATA',
            data: { userDetails }
        });
        dispatch({
            type: 'SET_CURRENT_STEP',
            step: WIZARD_STEPS.CHATBOT_TEST
        });
    };

    const handleNeedHelp = () => {
        // Save current form data
        dispatch({
            type: 'UPDATE_WIZARD_DATA',
            data: { userDetails }
        });
        
        // Store current step for return navigation
        dispatch({
            type: 'SET_PREVIOUS_STEP',
            step: WIZARD_STEPS.USER_DETAILS
        });
        dispatch({
            type: 'SET_CURRENT_STEP',
            step: WIZARD_STEPS.WHITE_GLOVE_SERVICE
        });
    };

    // Email verification handlers - Enhanced with subscription check
    const handleSendVerificationCode = async () => {
        if (!validateEmail(userDetails.email)) {
            dispatch({
                type: 'UPDATE_EMAIL_VERIFICATION',
                data: {
                    error: 'Please enter a valid email address first'
                }
            });
            return;
        }

        dispatch({
            type: 'UPDATE_EMAIL_VERIFICATION',
            data: {
                loading: true,
                error: null,
                message: 'Checking your account...'
            }
        });

        try {
            // Step 1: Check if email exists in subscription table (only if no JWT token)
            let existingClientId = wizardData.clientId; // Use JWT clientId if available
            
            if (!existingClientId) {
                console.log('[EMAIL_VERIFICATION] No JWT token found, checking for existing subscription...');
                
                try {
                    const subscriptionCheck = await checkEmailSubscription(userDetails.email);
                    
                    if (subscriptionCheck.data?.isExistingClient) {
                        existingClientId = subscriptionCheck.data.clientId;
                        console.log(`[EMAIL_VERIFICATION] Found existing client: ${existingClientId}`);
                        
                        // Store the found clientId in wizard data
                        dispatch({
                            type: 'UPDATE_WIZARD_DATA',
                            data: {
                                clientId: existingClientId,
                                isExistingClient: true,
                                clientName: subscriptionCheck.data.clientName,
                                packageName: subscriptionCheck.data.packageName
                            }
                        });
                        
                        // SECURITY: Always require email verification for non-JWT users
                        // Even if existing subscription found, must verify email ownership
                        dispatch({
                            type: 'UPDATE_EMAIL_VERIFICATION',
                            data: {
                                isVerified: false,
                                codeSent: false,
                                loading: false,
                                message: `Welcome back! Sending verification code to confirm your ${subscriptionCheck.data.packageName} subscription.`
                            }
                        });
                        
                        console.log('[EMAIL_VERIFICATION] Existing subscription found, but still requiring email verification for security');
                    } else {
                        console.log('[EMAIL_VERIFICATION] No existing subscription found, proceeding as new user');
                        dispatch({
                            type: 'UPDATE_EMAIL_VERIFICATION',
                            data: {
                                isVerified: false,
                                codeSent: false,
                                loading: false,
                                message: 'New user detected, sending verification code...'
                            }
                        });
                    }
                } catch (subscriptionError) {
                    // If it's a disposable email error, don't proceed - throw it to outer catch
                    if (subscriptionError.message?.startsWith('DISPOSABLE_EMAIL:')) {
                        throw subscriptionError; // Re-throw to be caught by outer error handler
                    }
                    
                    // For other subscription check errors, proceed with verification
                    console.warn('[EMAIL_VERIFICATION] Subscription check failed, proceeding with verification:', subscriptionError);
                    dispatch({
                        type: 'UPDATE_EMAIL_VERIFICATION',
                        data: {
                            isVerified: false,
                            codeSent: false,
                            loading: false,
                            message: 'Sending verification code...'
                        }
                    });
                }
            }

            // Step 2: Send verification code (always required for security when no JWT token)
            console.log('[EMAIL_VERIFICATION] Sending verification code...');
            
            const response = await sendVerificationCode({
                email: userDetails.email,
                clientId: existingClientId || wizardData.clientId
            });

            dispatch({
                type: 'UPDATE_EMAIL_VERIFICATION',
                data: {
                    loading: false,
                    codeSent: true,
                    sessionId: response.data.sessionId,
                    // SECURITY FIX: Only trust isAdminPanelUser if user came from admin panel with JWT
                    // Don't allow backend to bypass verification for users with existing subscriptions
                    isAdminPanelUser: wizardData.prefilledFromAdmin || false,
                    error: null,
                    message: null
                }
            });

            console.log('✅ Verification code sent successfully');
        } catch (error) {
            console.error('❌ Failed to send verification code:', error);
            
            let errorMessage = 'Failed to send verification code. Please try again.';
            
            if (error.message.startsWith('RATE_LIMITED:')) {
                const parts = error.message.split(':');
                const minutes = parts[1];
                errorMessage = `Too many requests. Please try again in ${minutes} minutes.`;
            } else if (error.message.startsWith('VALIDATION_ERROR:')) {
                errorMessage = error.message.split(':')[1];
            } else if (error.message.startsWith('TIMEOUT:')) {
                errorMessage = 'Request timed out. Please try again.';
            } else if (error.message.startsWith('DISPOSABLE_EMAIL:')) {
                // Extract the user-friendly message from the backend
                errorMessage = error.message.split(':')[1];
            }

            dispatch({
                type: 'UPDATE_EMAIL_VERIFICATION',
                data: {
                    loading: false,
                    error: errorMessage,
                    message: null
                }
            });
        }
    };

    const handleVerifyCode = async () => {
        const code = wizardData.emailVerification.verificationCode;
        
        if (!code || code.length !== 6) {
            dispatch({
                type: 'UPDATE_EMAIL_VERIFICATION',
                data: {
                    error: 'Please enter a valid 6-digit code'
                }
            });
            return;
        }

        dispatch({
            type: 'UPDATE_EMAIL_VERIFICATION',
            data: {
                loading: true,
                error: null
            }
        });

        try {
            await verifyEmailCode({
                email: userDetails.email,
                code: code,
                sessionId: wizardData.emailVerification.sessionId
            });

            dispatch({
                type: 'UPDATE_EMAIL_VERIFICATION',
                data: {
                    loading: false,
                    isVerified: true,
                    error: null
                }
            });

            console.log('✅ Email verified successfully');
        } catch (error) {
            console.error('❌ Failed to verify code:', error);
            
            let errorMessage = 'Invalid verification code. Please try again.';
            
            if (error.message.startsWith('RATE_LIMITED:')) {
                const parts = error.message.split(':');
                const minutes = parts[1];
                errorMessage = `Too many attempts. Please try again in ${minutes} minutes.`;
            } else if (error.message.startsWith('CODE_EXPIRED:')) {
                errorMessage = error.message.split(':')[1];
                // Reset to allow sending new code
                dispatch({
                    type: 'UPDATE_EMAIL_VERIFICATION',
                    data: {
                        codeSent: false,
                        verificationCode: '',
                        sessionId: null
                    }
                });
            } else if (error.message.startsWith('INVALID_CODE:')) {
                const parts = error.message.split(':');
                const message = parts[1];
                const remaining = parts[2];
                errorMessage = `${message}${remaining ? ` (${remaining} attempts remaining)` : ''}`;
            } else if (error.message.startsWith('VALIDATION_ERROR:')) {
                errorMessage = error.message.split(':')[1];
            }

            dispatch({
                type: 'UPDATE_EMAIL_VERIFICATION',
                data: {
                    loading: false,
                    error: errorMessage
                }
            });
        }
    };

    const handleCodeInputChange = (event) => {
        const value = event.target.value.replace(/\D/g, '').slice(0, 6); // Only digits, max 6
        dispatch({
            type: 'UPDATE_EMAIL_VERIFICATION',
            data: {
                verificationCode: value,
                error: null // Clear error when user types
            }
        });
    };

    const handlePublish = async () => {
        const newErrors = {};

        // Validation - Only Email and Terms for now
        /* Commented out for now - First Name, Last Name, and Company validation
        if (!userDetails.firstName.trim()) {
            newErrors.firstName = 'First name is required';
        }
        if (!userDetails.lastName.trim()) {
            newErrors.lastName = 'Last name is required';
        }
        */
        // Only validate email if not pre-filled from admin panel
        if (!wizardData.prefilledFromAdmin) {
            if (!userDetails.email.trim()) {
                newErrors.email = 'Email is required';
            } else if (!validateEmail(userDetails.email)) {
                newErrors.email = 'Please enter a valid email address';
            }
        }

        // Email verification validation - Skip for admin panel users
        if (!wizardData.emailVerification.isVerified && !wizardData.emailVerification.isAdminPanelUser) {
            newErrors.general = 'Please verify your email address before publishing your agent.';
        }
        /* Commented out for now - Company validation
        if (!userDetails.company.trim()) {
            newErrors.company = 'Company name is required';
        }
        */
        if (!agreedToTerms) {
            newErrors.terms = 'Please agree to the terms and conditions';
        }

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        try {
            setIsLoading(true);
            
            // Prepare comprehensive publish data
            const publishData = {
                // Agent Information
                chatbotId: wizardData.chatbotId,
                chatbotName: wizardData.chatbotName,
                skillDescription: wizardData.skillDescription,
                chatbotUrl: wizardData.chatbotUrl,
                
                // Client ID for admin panel integration
                clientId: wizardData.clientId || null,
                prefilledFromAdmin: wizardData.prefilledFromAdmin || false,
                currentAgents: wizardData.currentAgents || 0,
                
                // User Details - Only email for now
                userDetails: {
                    /* Commented out for now
                    firstName: userDetails.firstName,
                    lastName: userDetails.lastName,
                    company: userDetails.company,
                    */
                    email: userDetails.email
                },
                
                // Content Information
                files: wizardData.files.map(file => ({
                    name: file.name,
                    size: file.size,
                    type: file.type,
                    uploadedAt: file.uploadedAt || new Date().toISOString()
                })),
                totalFiles: wizardData.files.length,
                
                // Publishing Configuration
                status: 'online',
                publishedAt: new Date().toISOString(),
                agreedToTerms,
                
                // Additional Metadata
                wizardVersion: '1.0',
                createdAt: wizardData.createdAt || new Date().toISOString(),
                lastModified: new Date().toISOString(),
                
                // Optional fields for future use
                webhookUrl: userDetails.webhookUrl || null,
                customDomain: userDetails.customDomain || null,
                analyticsEnabled: true,
                publicAccess: true
            };

            // Call comprehensive publish API (it will handle both BigQuery and Firestore updates)
            console.log('Publishing agent with comprehensive data...');
            const publishResponse = await publishAgent(publishData);
            
            console.log('🔍 DEBUG: Full publishResponse:', publishResponse);
            console.log('🔍 DEBUG: publishResponse.data:', publishResponse.data);
            
            // Save user details and publish results
            dispatch({
                type: 'UPDATE_WIZARD_DATA',
                data: { 
                    userDetails,
                    isPublished: true,
                    publishedAt: new Date().toISOString(),
                    publishResponse: publishResponse.data
                }
            });

            // Navigate to success page
            dispatch({
                type: 'SET_VIEW',
                view: VIEW.SUCCESS
            });
            
        } catch (error) {
            console.error('Error publishing agent:', error);
            
            // Extract user-friendly error message from API response
            let errorMessage = 'Failed to publish agent. Please try again.';
            let isSubscriptionExpired = false;
            let isTokenExpired = false;
            
            if (error.message) {
                // Use the specific error message from the API
                errorMessage = error.message;
            } else if (error.response?.data?.message) {
                // Fallback to response data message
                errorMessage = error.response.data.message;
            }
            
            // Check if this is a JWT token expiration error
            if (error.response?.status === 401 && 
                (errorMessage.toLowerCase().includes('wizard token has expired') ||
                 errorMessage.toLowerCase().includes('token has expired') ||
                 errorMessage.toLowerCase().includes('expired wizard token') ||
                 error.response?.data?.wizardTokenError?.includes('expired'))) {
                isTokenExpired = true;
                errorMessage = 'Your wizard session has expired (1 hour limit). Please go back to the admin panel and generate a new wizard link to publish your agent.';
            }
            
            // Check if this is a subscription expiration error
            if (error.response?.data?.subscriptionExpired === true || 
                errorMessage.toLowerCase().includes('subscription has expired') ||
                errorMessage.toLowerCase().includes('subscription expired')) {
                isSubscriptionExpired = true;
            }
            
            setErrors({ 
                general: errorMessage,
                subscriptionExpired: isSubscriptionExpired,
                tokenExpired: isTokenExpired
            });
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Box ref={containerRef} className={classes.stepContainer}>
            {/* Publishing Overlay */}
            {isLoading && (
                <Box className={classes.publishingOverlay}>
                    <CircularProgress size={60} />
                    <Typography className={classes.publishingMessage}>
                        Publishing…
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                        This usually only takes a moment
                    </Typography>
                </Box>
            )}

            <Box className={classes.header}>
                <Typography className={classes.title}>
                    Publish your agent
                </Typography>
                <Typography className={classes.description}>
                    Verify your email to make it available. You can keep refining it after you publish.
                </Typography>
            </Box>

            <Card className={classes.formCard} elevation={0}>
                <CardContent>
                    <Typography className={classes.sectionLabel}>
                        Email
                    </Typography>

                    {wizardData.prefilledFromAdmin ? (
                        <Box className={classes.prefilledEmailBox}>
                            <Box>
                                <Typography className={classes.prefilledEmailText}>
                                    {userDetails.email}
                                </Typography>
                                <Typography className={classes.prefilledEmailSubtext}>
                                    Signed in from the admin panel
                                </Typography>
                            </Box>
                            <Box className={classes.adminBadge}>
                                Admin
                            </Box>
                        </Box>
                    ) : (
                        <TextField
                            label="Work email"
                            variant="outlined"
                            type="email"
                            value={userDetails.email}
                            onChange={handleInputChange('email')}
                            error={!!errors.email}
                            helperText={errors.email}
                            className={classes.fullWidthField}
                            fullWidth
                            required
                        />
                    )}

                    {!wizardData.emailVerification.isAdminPanelUser && (
                        <Box className={classes.verificationSection}>
                            <Box className={classes.verificationTitle}>
                                <Typography style={{ fontSize: '0.95rem', fontWeight: 600 }}>
                                    Verify your email
                                </Typography>
                                
                                {!wizardData.emailVerification.isVerified && 
                                 !wizardData.emailVerification.codeSent && 
                                 validateEmail(userDetails.email) && (
                                    <Button
                                        variant="contained"
                                        onClick={handleSendVerificationCode}
                                        disabled={wizardData.emailVerification.loading}
                                        className={classes.sendCodeButton}
                                        disableElevation
                                    >
                                        {wizardData.emailVerification.loading ? 'Sending…' : 'Send code'}
                                    </Button>
                                )}
                            </Box>

                            {wizardData.emailVerification.isVerified && (
                                <Box className={classes.verificationSuccess}>
                                    <Typography variant="body2" style={{ fontWeight: 500 }}>
                                        Email verified. You’re ready to publish.
                                    </Typography>
                                </Box>
                            )}

                            {!wizardData.emailVerification.isVerified && wizardData.emailVerification.codeSent && (
                                <>
                                    <Typography variant="body2" style={{ marginBottom: 16, color: '#6B747F' }}>
                                        Enter the 6-digit code we sent to your inbox.
                                    </Typography>
                                    
                                    <Box className={classes.codeInputContainer}>
                                        <TextField
                                            label="Verification code"
                                            variant="outlined"
                                            value={wizardData.emailVerification.verificationCode}
                                            onChange={handleCodeInputChange}
                                            placeholder="000000"
                                            className={classes.codeInput}
                                            inputProps={{
                                                maxLength: 6,
                                                style: { textAlign: 'center' }
                                            }}
                                        />
                                        <Button
                                            variant="contained"
                                            onClick={handleVerifyCode}
                                            disabled={wizardData.emailVerification.verificationCode.length !== 6 || wizardData.emailVerification.loading}
                                            className={classes.verifyButton}
                                            disableElevation
                                        >
                                            {wizardData.emailVerification.loading ? 'Verifying…' : 'Verify'}
                                        </Button>
                                        <Button
                                            variant="outlined"
                                            onClick={handleSendVerificationCode}
                                            disabled={wizardData.emailVerification.loading}
                                            className={classes.resendButton}
                                        >
                                            Resend
                                        </Button>
                                    </Box>
                                </>
                            )}

                            {wizardData.emailVerification.error && (
                                <Box className={classes.verificationError}>
                                    <Typography variant="body2">
                                        {wizardData.emailVerification.error}
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    )}

                    {wizardData.emailVerification.isAdminPanelUser && (
                        <Box className={classes.verificationSuccess}>
                            <Typography variant="body2" style={{ fontWeight: 500 }}>
                                Email verified. You’re ready to publish.
                            </Typography>
                        </Box>
                    )}
                </CardContent>
            </Card>

            <Box className={classes.infoBox}>
                <Typography className={classes.infoTitle}>
                    After you publish
                </Typography>
                <ul className={classes.infoList}>
                    <li>Your agent gets a public link you can share</li>
                    <li>You can open the admin panel to edit content and settings</li>
                    <li>Usage and conversations will show up in your dashboard</li>
                </ul>
            </Box>

            <Card className={classes.termsCard} elevation={0}>
                <CardContent style={{ padding: '8px 4px' }}>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={agreedToTerms}
                                onChange={(e) => {
                                    setAgreedToTerms(e.target.checked);
                                    if (errors.terms) {
                                        setErrors(prev => ({ ...prev, terms: '' }));
                                    }
                                }}
                                color="primary"
                            />
                        }
                        label={
                            <Typography variant="body2" color="textSecondary">
                                I agree to the{' '}
                                <a href="https://sail.cx/terms-and-conditions-of-use/" target="_blank" rel="noopener noreferrer">
                                    Terms of Service
                                </a>{' '}
                                and{' '}
                                <a href="https://sail.cx/privacy-policy/" target="_blank" rel="noopener noreferrer">
                                    Privacy Policy
                                </a>
                            </Typography>
                        }
                    />
                    {errors.terms && (
                        <Typography color="error" variant="body2" style={{ marginTop: 4 }}>
                            {errors.terms}
                        </Typography>
                    )}
                </CardContent>
            </Card>

            {/* Display general errors */}
            {errors.general && (
                <Box style={{ 
                    margin: '16px 0', 
                    padding: '16px', 
                    backgroundColor: '#ffebee', 
                    border: '1px solid #f44336', 
                    borderRadius: '8px',
                    textAlign: 'left'
                }}>
                    <Typography variant="body2" style={{ color: '#d32f2f', marginBottom: '8px', fontWeight: 600 }}>
                        Something went wrong
                    </Typography>
                    <Typography variant="body2" style={{ color: '#d32f2f', marginBottom: (errors.subscriptionExpired || errors.tokenExpired) ? '16px' : '0' }}>
                        {errors.general}
                    </Typography>
                    
                    {/* Show renewal button for subscription expiration */}
                    {errors.subscriptionExpired && (
                        <Box style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginTop: '12px' }}>
                            <Link
                                href="https://sail.cx/pricing/"
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    padding: '10px 16px',
                                    backgroundColor: '#28a745',
                                    color: 'white',
                                    textDecoration: 'none',
                                    borderRadius: '6px',
                                    fontSize: '14px',
                                    fontWeight: 500,
                                    boxShadow: '0 2px 4px rgba(40, 167, 69, 0.3)',
                                    transition: 'all 0.2s ease',
                                    '&:hover': {
                                        backgroundColor: '#218838',
                                        transform: 'translateY(-1px)',
                                        boxShadow: '0 4px 8px rgba(40, 167, 69, 0.4)'
                                    }
                                }}
                            >
                                <CreditCard fontSize="small" />
                                Renew Subscription
                            </Link>
                        </Box>
                    )}
                    
                    {/* Show "Generate New Link" button for token expiration */}
                    {errors.tokenExpired && (
                        <Box style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginTop: '12px' }}>
                            <Button
                                variant="contained"
                                onClick={() => {
                                    // Clear the expired token and guide user
                                    sessionStorage.removeItem('wizardToken');
                                    window.alert('Please go to your admin panel to generate a new wizard link. Your session expired after 1 hour for security.');
                                }}
                                style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    padding: '10px 16px',
                                    backgroundColor: '#ff9800',
                                    color: 'white',
                                    borderRadius: '6px',
                                    fontSize: '14px',
                                    fontWeight: 500,
                                    boxShadow: '0 2px 4px rgba(255, 152, 0, 0.3)'
                                }}
                            >
                                Generate new link
                            </Button>
                        </Box>
                    )}
                </Box>
            )}

            <Box className={classes.buttonContainer}>
                <Box className={classes.leftActions}>
                    <Button
                        onClick={handleBackToTest}
                        className={classes.backButton}
                        startIcon={<ArrowBack />}
                        disabled={isLoading}
                    >
                        Back to test
                    </Button>
                    <Button
                        onClick={handleNeedHelp}
                        className={classes.helpButton}
                        disabled={isLoading}
                    >
                        Need help?
                    </Button>
                </Box>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handlePublish}
                    className={classes.publishButton}
                    disableElevation
                    disabled={isLoading || (!wizardData.emailVerification.isVerified && !wizardData.emailVerification.isAdminPanelUser)}
                >
                    {isLoading ? 'Publishing…' : 
                     (!wizardData.emailVerification.isVerified && !wizardData.emailVerification.isAdminPanelUser) ? 'Verify email to publish' : 
                     'Publish'}
                </Button>
            </Box>
        </Box>
    );
}
